import { useState } from 'react';

import { Card } from '@components/card';
import { Checkbox } from '@components/checkbox';
import { UploadedFile } from '@components/file-field';
import { createForm, useCreateFormStore } from '@components/form';
import { HideContainer } from '@components/hide-container';
import { IconFile } from '@components/icons/file';
import { IconHourglass } from '@components/icons/hourglass';
import { IconReplace } from '@components/icons/replace';
import { LabelTextGroup } from '@components/label-text-group';
import { Spinner } from '@components/loading-spinner';
import { usePreviewFileModal } from '@components/modals/preview-file-modal';
import { NoResultCard } from '@components/no-result-card';
import { SelectOption } from '@components/select';
import { Sidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import {
  FieldMappingEditorValue,
  FieldMappingVariables,
} from '@components/text-editors/addons/field-mapping';
import { EditorValue } from '@components/text-editors/text-editor';
import { useScopedTranslate } from '@locale/translate';
import { fileNameFromPath } from '@utils/strings';

import { useConvertedDocument } from 'api/documents/hooks';
import { createTravellerJourneyActionDocumentPath } from 'api/documents/requests';
import { useUploadFile } from 'api/files/hooks';
import {
  useJourneyTravellerAction,
  useUpdateJourneyTravellerAction,
} from 'api/traveller-actions/hooks';
import {
  JourneyActionValuesTypes,
  MappableActionVariable,
  TravellerJourneyActionDetailed,
} from 'api/types';
import { ExecutionMethod } from 'ui/journey-builder/execution-method';
import { DescriptionFormField, useVariableGroups } from 'ui/journey-builder/node-types/common';
import { FormWithUnsavedChangesPrompt } from 'ui/journey-builder/unsaved-changes-prompt';
import { useNodes } from 'ui/traveller-journey-builder/hooks/use-nodes';
import { actionErrorTitle } from 'ui/traveller-journey-builder/node-types/common';

import {
  ActionProgressBar,
  EditableActionContent,
  FormSidebarFooter,
  MappedSource,
  SidebarProps,
  TravellerActionActions,
  TravellerActionFooter,
  TriggerMethod,
  isActionError,
  useEditingContext,
} from '../common';
import { getFieldMappingId } from '../helpers';

type ActionType = 'convert_document';

export const SidebarContent = ({ shortId }: SidebarProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const travellerActionQuery = useJourneyTravellerAction<ActionType>(shortId);
  if (travellerActionQuery.isLoading) {
    return <Spinner />;
  }
  if (!shortId || !travellerActionQuery.data) {
    return <div>{t('dataNotFound')}</div>;
  }
  const action = travellerActionQuery.data;
  return (
    <Sidebar>
      <Sidebar.SimpleHeader title={t('title')} icon={IconReplace} iconColor="elementBlue100">
        <TravellerActionActions action={action} />
      </Sidebar.SimpleHeader>
      <Sidebar.Content>
        <EditableActionContent
          edit={<FormContent action={action} />}
          display={<DetailsContent action={action} />}
        />
      </Sidebar.Content>
    </Sidebar>
  );
};

type ContentProps = {
  action: TravellerJourneyActionDetailed<ActionType>;
};

const DetailsContent = ({ action }: ContentProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const showFilePreviewModal = usePreviewFileModal();
  const referenceVariables = action.values?.document?.variables ?? {};
  const firstReferenceVariable = Object.values(referenceVariables)[0];
  const referenceVariablesKeys = Object.keys(referenceVariables);
  const isAttributeReference =
    referenceVariablesKeys.length > 0 &&
    (firstReferenceVariable.source.startsWith('company_traveller_attribute') ||
      firstReferenceVariable.source.startsWith('journey_attribute'));
  const attributeReferenceFile = isAttributeReference ? action.data.document : '';
  const rawReferenceActionId =
    !isAttributeReference && referenceVariablesKeys.length > 0
      ? (referenceVariables[referenceVariablesKeys[0]] as MappableActionVariable).action
      : '';

  const referenceActionId = rawReferenceActionId.replace(/_MISSING$/, '');
  const referenceActionQuery = useJourneyTravellerAction(referenceActionId);
  const referenceAction = referenceActionQuery.data;
  const hasReferenceRun = Boolean(referenceAction?.result);

  const convertedDocumentQuery = useConvertedDocument(action.shortId);
  const isError = isActionError(action);

  const filename = fileNameFromPath(convertedDocumentQuery.data?.file);
  const showMappedSource =
    isAttributeReference || Boolean(referenceActionId) || !!action.data.document;

  const ReferenceActionStatusCard = () =>
    referenceAction && referenceAction.status !== 'completed' ? (
      <Card variant="gray" space={4}>
        <Stack space={4} orientation="horizontal" align="center">
          <IconHourglass color="elementPurple60" />
          <Text font="semibold14" color="textSecondary" text={t('actionHasNotRun')} />
        </Stack>
      </Card>
    ) : null;

  if (referenceActionId && referenceActionQuery.isLoading) {
    return <Spinner />;
  }

  return (
    <Stack space={6}>
      <HideContainer title={t('actionStatus')}>
        <ActionProgressBar
          action={action}
          isError={isError}
          errorMessage={
            isError
              ? {
                  title: actionErrorTitle[action.action],
                  description: action.result?.message,
                  date: action.result?.dateFailed,
                }
              : undefined
          }
          dateSuccess={action.result?.dateSucceeded}
        />
      </HideContainer>
      <Sidebar.Separator />
      <HideContainer title={t('actionDetails')}>
        <Stack space={4}>
          <LabelTextGroup label={t('description')} text={action.description} />
          <Text font="semibold14" text={t('documentSource')} />
          {showMappedSource && (
            <MappedSource
              label={firstReferenceVariable ? firstReferenceVariable.label : t('document')}
              isAttribute={isAttributeReference}
              isActionMapped={Boolean(referenceActionId)}
              isUploaded={!isAttributeReference && !referenceActionId && !!action.data.document}
              referenceActionId={referenceActionId}
            />
          )}

          {referenceAction ? (
            <ReferenceActionStatusCard />
          ) : action.data.document ? (
            <UploadedFile
              name={
                isAttributeReference
                  ? attributeReferenceFile.name
                  : fileNameFromPath(action.data.document)
              }
              path={isAttributeReference ? attributeReferenceFile.src : ''}
              onPreview={
                isAttributeReference
                  ? () => {
                      showFilePreviewModal({
                        formFile: attributeReferenceFile,
                        filesList: [attributeReferenceFile],
                      });
                    }
                  : undefined
              }
              iconColor={isAttributeReference ? 'elementBlue100' : 'elementPurpleInvert'}
              font="regular14"
            />
          ) : (
            <Text font="regular14" color="elementPurple50" text={t('core.not_set')} />
          )}
        </Stack>
      </HideContainer>
      <Sidebar.Separator />
      <HideContainer title={t('documentOutput')}>
        {convertedDocumentQuery.data?.file ? (
          <UploadedFile
            name={filename}
            path={convertedDocumentQuery.data?.file}
            iconColor="elementPurpleInvert"
            font="regular14"
            onPreview={() => {
              showFilePreviewModal({
                formFile: {
                  src: convertedDocumentQuery.data?.file,
                  name: filename,
                },
                filesList: [{ src: convertedDocumentQuery.data?.file, name: filename }],
              });
            }}
          />
        ) : (
          <NoResultCard
            icon={IconFile}
            text={referenceAction && !hasReferenceRun ? t('documentNotReady') : t('actionNotRun')}
          />
        )}
      </HideContainer>
      <Sidebar.Separator />
      <TravellerActionFooter
        action={action}
        startIcon={<IconReplace />}
        startText={t('convert')}
        retryText={t('retry')}
      />
    </Stack>
  );
};

type ConvertDocumentData = {
  document: EditorValue<{ variables?: FieldMappingVariables }>;
  name: FieldMappingEditorValue;
  description: string;
  isAutomatic: boolean;
  assignees: SelectOption[];
  executionMethod: TriggerMethod;
};

const Form = createForm();

const FormContent = ({ action }: ContentProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const { stopEditing } = useEditingContext();
  const formStore = useCreateFormStore();
  const { updateSelectedNodeData } = useNodes();
  const values = action.values as JourneyActionValuesTypes['convert_document'];
  const [rename, setRename] = useState(Boolean(values?.name));
  const isFormDirty = formStore.useIsDirty();
  const isDirty = isFormDirty || rename !== Boolean(values?.name);
  const baseVariableGroups = useVariableGroups({
    actionVariables: action.actionVariables,
    journeyId: action.journeyId,
  });
  const fileVariableGroups = useVariableGroups({
    actionVariables: action.actionVariables,
    journeyId: action.journeyId,
    types: ['file'],
  });
  const fieldMappingId = getFieldMappingId(action.journeyId);
  const uploadMutation = useUploadFile(createTravellerJourneyActionDocumentPath(action), true);

  const editMutation = useUpdateJourneyTravellerAction(action, {
    onSuccess: stopEditing,
  });

  const handleSave = async (data: ConvertDocumentData) => {
    await editMutation.mutateAsync({
      description: data.description,
      isAutomatic: data.executionMethod === 'automatic',
      assignees: data.executionMethod === 'manual' ? data.assignees.map((user) => user.value) : [],
      values: {
        document: data.document,
        name: rename ? data.name : undefined,
      },
    });
  };

  return (
    <FormWithUnsavedChangesPrompt store={formStore} onSubmit={handleSave}>
      <Stack space={6}>
        <HideContainer title={t('actionDetails')}>
          <DescriptionFormField isTraveller defaultValue={action.description} />
        </HideContainer>
        <Sidebar.Separator />
        <HideContainer title={t('triggerMethod')}>
          <ExecutionMethod
            assignees={action.assignees}
            isAutomatic={action.isAutomatic}
            hideLabel
            onChange={(isAutomatic) => updateSelectedNodeData({ isAutomatic })}
            formStore={formStore}
          />
        </HideContainer>
        <Sidebar.Separator />
        <HideContainer title={t('document')}>
          <Form.FieldMappingFileField
            id="document"
            label={t('document')}
            defaultValue={action.values?.document?.value ? action.values.document : undefined}
            variableGroups={fileVariableGroups}
            onSelect={uploadMutation.mutateAsync}
            fileType="document"
            fieldMappingId={fieldMappingId}
          />
        </HideContainer>
        <Sidebar.Separator />
        <HideContainer title={t('advanced')} defaultClosed={!rename}>
          <Stack space={4}>
            <Checkbox label={t('renameOutput')} onChange={setRename} defaultValue={rename} />
            {rename && (
              <Form.FieldMappingTextEditor
                id="name"
                plaintext
                singleLine
                label={t('fileOutputName')}
                defaultValue={values?.name}
                variableGroups={baseVariableGroups}
                validators={['required']}
                fieldMappingId={fieldMappingId}
              />
            )}
          </Stack>
        </HideContainer>
        <FormSidebarFooter Form={Form} isDirty={isDirty} editMutation={editMutation} />
      </Stack>
    </FormWithUnsavedChangesPrompt>
  );
};
const sidebarLocale = {
  en: {
    title: 'Convert document to PDF   ',
    documentLabel: 'Document',
    document: 'Document',
    dataNotFound: 'Data not found',
    description: 'Description',
    automatic: 'Automatic',
    manual: 'Manual',
    details: 'Details',
    file: 'File',
    started: 'Started',
    completed: 'Completed',
    retry: 'Re-create',
    template: 'Template',
    inProgress: 'In progress',
    error: 'Error',
    notStarted: 'Not started',
    documentConverted: 'Document converted',
    actionNotRun: 'Action has not been run',
    documentNotReady: 'Document not ready',
    convert: 'Convert',
    convertedDocument: 'Converted document',
    actionDetails: 'Action details',
    triggerMethod: 'Trigger method',
    advanced: 'Advanced',
    renameOutput: 'Rename document output',
    fileOutputName: 'File output name',
    actionStatus: 'Action status',
    documentOutput: 'Document output',
    documentSource: 'Document source',
    actionHasNotRun: 'Action has not been run',
  },
};
